import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  RotateCcw,
  SlidersHorizontal,
} from "lucide-react";
import { Input as AntInput, Select } from "antd";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import {
  LEAVE_APPROVAL_STATUS_OPTIONS,
  LEAVE_TYPE_OPTIONS,
  leaveStatusLabel,
} from "./utils";
import {
  FILTER_SELECT_CLASS,
  EMPTY_LEAVE_REQUEST_FILTERS,
  type LeaveRequestFilterValues,
} from "./LeaveRequestFilters.constants";

interface LeaveRequestFiltersProps {
  values: LeaveRequestFilterValues;
  onChange: (values: LeaveRequestFilterValues) => void;
  hideStatusFilter?: boolean;
  idPrefix?: string;
}

const LeaveRequestFilters = ({
  values,
  onChange,
  hideStatusFilter = false,
  idPrefix = "leave-request",
}: LeaveRequestFiltersProps) => {
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const { startDate, endDate, leaveType, status, search } = values;

  const leaveTypeOptions = useMemo(
    () =>
      LEAVE_TYPE_OPTIONS.map(option => ({
        value: option.value,
        label: option.label,
      })),
    []
  );

  const statusOptions = useMemo(
    () =>
      LEAVE_APPROVAL_STATUS_OPTIONS.map(value => ({
        value,
        label: leaveStatusLabel(value),
      })),
    []
  );

  const handleResetFilters = () => {
    onChange({ ...EMPTY_LEAVE_REQUEST_FILTERS });
  };

  return (
    <Collapsible open={filtersExpanded} onOpenChange={setFiltersExpanded}>
      <Card className="border border-gray-200 bg-white shadow-sm">
        <CardContent className="px-4 py-2">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="hover:bg-muted/40 flex w-full items-center justify-between rounded-md text-left transition-colors"
              aria-expanded={filtersExpanded}
              aria-label="Toggle filters"
            >
              <div className="flex items-center gap-2 py-1">
                <SlidersHorizontal className="text-muted-foreground h-4 w-4" />
                <span className="text-sm font-semibold text-gray-800">
                  Filters
                </span>
              </div>
              {filtersExpanded ? (
                <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />
              ) : (
                <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
              )}
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="border-t border-gray-100 pb-2 pt-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
              <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:flex-1 xl:grid-cols-5">
                <div className="flex w-full min-w-0 flex-col gap-1.5">
                  <Label
                    htmlFor={`${idPrefix}-start-date`}
                    className="text-xs font-medium text-gray-700"
                  >
                    Start date
                  </Label>
                  <Input
                    id={`${idPrefix}-start-date`}
                    type="date"
                    value={startDate ?? ""}
                    onChange={e =>
                      onChange({
                        ...values,
                        startDate: e.target.value || undefined,
                      })
                    }
                    className="h-9 bg-[#fafafa]"
                  />
                </div>

                <div className="flex w-full min-w-0 flex-col gap-1.5">
                  <Label
                    htmlFor={`${idPrefix}-end-date`}
                    className="text-xs font-medium text-gray-700"
                  >
                    End date
                  </Label>
                  <Input
                    id={`${idPrefix}-end-date`}
                    type="date"
                    value={endDate ?? ""}
                    onChange={e =>
                      onChange({
                        ...values,
                        endDate: e.target.value || undefined,
                      })
                    }
                    className="h-9 bg-[#fafafa]"
                  />
                </div>

                <div className="flex w-full min-w-0 flex-col gap-1.5">
                  <Label
                    htmlFor={`${idPrefix}-leave-type`}
                    className="text-xs font-medium text-gray-700"
                  >
                    Leave type
                  </Label>
                  <Select
                    id={`${idPrefix}-leave-type`}
                    allowClear
                    placeholder="All leave types"
                    value={leaveType ?? undefined}
                    onChange={value =>
                      onChange({
                        ...values,
                        leaveType: value || undefined,
                      })
                    }
                    className={FILTER_SELECT_CLASS}
                    style={{ width: "100%" }}
                    options={leaveTypeOptions}
                    listHeight={280}
                    popupMatchSelectWidth
                    getPopupContainer={trigger =>
                      trigger.parentElement ?? document.body
                    }
                  />
                </div>

                {!hideStatusFilter ? (
                  <div className="flex w-full min-w-0 flex-col gap-1.5">
                    <Label
                      htmlFor={`${idPrefix}-status`}
                      className="text-xs font-medium text-gray-700"
                    >
                      Status
                    </Label>
                    <Select
                      id={`${idPrefix}-status`}
                      allowClear
                      placeholder="All statuses"
                      value={status ?? undefined}
                      onChange={value =>
                        onChange({
                          ...values,
                          status: value || undefined,
                        })
                      }
                      className={FILTER_SELECT_CLASS}
                      style={{ width: "100%" }}
                      options={statusOptions}
                      listHeight={280}
                      popupMatchSelectWidth
                      getPopupContainer={trigger =>
                        trigger.parentElement ?? document.body
                      }
                    />
                  </div>
                ) : null}

                <div className="flex w-full min-w-0 flex-col gap-1.5">
                  <Label
                    htmlFor={`${idPrefix}-search`}
                    className="text-xs font-medium text-gray-700"
                  >
                    Search
                  </Label>
                  <AntInput
                    id={`${idPrefix}-search`}
                    allowClear
                    placeholder="Search by name..."
                    value={search ?? ""}
                    onChange={e =>
                      onChange({
                        ...values,
                        search: e.target.value || undefined,
                      })
                    }
                    className="h-9 rounded-lg border-gray-200 bg-[#fafafa]"
                  />
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 w-full shrink-0 lg:w-[110px]"
                onClick={handleResetFilters}
              >
                <RotateCcw className="mr-1.5 h-4 w-4" />
                Reset
              </Button>
            </div>
          </CollapsibleContent>
        </CardContent>
      </Card>
    </Collapsible>
  );
};

export default LeaveRequestFilters;
